import { getAccessProfileQueryKey } from "@packages/api-client/hooks/authorizationController";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	platformAiBudgetQueryKey,
	platformCostSummaryQueryKey,
	platformSubscriptionQueryKey,
	platformUsageQueryKey,
	syncPlatformSubscription,
} from "@/api/platformBilling";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/settings/platform-plan/success",
)({
	component: PlatformPlanSuccessPage,
});

type SyncState = "loading" | "success" | "error";

const REDIRECT_COUNTDOWN_SECONDS = 5;

/**
 * PlatformPlanSuccessPage - Syncs subscription and shows result after checkout
 */
function PlatformPlanSuccessPage() {
	const { t } = useTranslation();
	const { orgId } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [syncState, setSyncState] = useState<SyncState>("loading");
	const [countdown, setCountdown] = useState(REDIRECT_COUNTDOWN_SECONDS);

	const handleRedirect = useCallback(() => {
		navigate({ to: "/app/org/$orgId", params: { orgId } });
	}, [navigate, orgId]);

	const doSync = useCallback(async () => {
		setSyncState("loading");
		try {
			await syncPlatformSubscription(orgId);
			// Invalidate queries to refresh subscription data. The access profile
			// query drives the subscription gate on /app/org/$orgId — without
			// refreshing it the dashboard redirect lands on the "inactive" page
			// until a full reload clears the in-memory cache.
			const [accessProfileBaseKey] = getAccessProfileQueryKey({
				organizationId: orgId,
			});
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: platformSubscriptionQueryKey(orgId),
				}),
				queryClient.invalidateQueries({
					queryKey: platformUsageQueryKey(orgId),
				}),
				// A top-up credit granted by the sync raises the AI budget — refresh
				// the breakdown and the cost summary so the new total shows on return.
				queryClient.invalidateQueries({
					queryKey: platformAiBudgetQueryKey(orgId),
				}),
				queryClient.invalidateQueries({
					queryKey: platformCostSummaryQueryKey(orgId),
				}),
				queryClient.invalidateQueries({
					queryKey: [accessProfileBaseKey],
				}),
			]);
			setSyncState("success");
		} catch (error) {
			console.error("Failed to sync subscription:", error);
			setSyncState("error");
		}
	}, [orgId, queryClient]);

	// Sync on mount
	useEffect(() => {
		doSync();
	}, [doSync]);

	// Countdown and auto-redirect after success
	useEffect(() => {
		if (syncState !== "success") return;

		if (countdown <= 0) {
			handleRedirect();
			return;
		}

		const timer = setTimeout(() => {
			setCountdown((prev) => prev - 1);
		}, 1000);

		return () => clearTimeout(timer);
	}, [syncState, countdown, handleRedirect]);

	// Loading state
	if (syncState === "loading") {
		return (
			<div className="max-w-2xl mx-auto px-4 py-16 text-center">
				<div className="bg-base-100 rounded-lg shadow-lg p-8">
					<div className="flex justify-center mb-6">
						<Loader2 className="h-20 w-20 text-primary animate-spin" />
					</div>
					<h1 className="text-3xl font-bold text-base-content mb-4">
						{t("platformPlan.success.syncing")}
					</h1>
					<p className="text-base-content/70">
						{t("platformPlan.success.syncingDescription")}
					</p>
				</div>
			</div>
		);
	}

	// Error state
	if (syncState === "error") {
		return (
			<div className="max-w-2xl mx-auto px-4 py-16 text-center">
				<div className="bg-base-100 rounded-lg shadow-lg p-8">
					<div className="flex justify-center mb-6">
						<AlertCircle className="h-20 w-20 text-warning" />
					</div>
					<h1 className="text-3xl font-bold text-base-content mb-4">
						{t("platformPlan.success.syncError")}
					</h1>
					<p className="text-base-content/70 mb-8">
						{t("platformPlan.success.syncErrorDescription")}
					</p>
					<div className="flex flex-col sm:flex-row gap-4 justify-center">
						<Button type="button" onClick={doSync}>
							{t("common.retry")}
						</Button>
						<Button variant="outline" asChild>
							<Link to="/app/org/$orgId" params={{ orgId }}>
								{t("platformPlan.success.cta")}
							</Link>
						</Button>
					</div>
				</div>
			</div>
		);
	}

	// Success state
	return (
		<div className="max-w-2xl mx-auto px-4 py-16 text-center">
			<div className="bg-base-100 rounded-lg shadow-lg p-8">
				<div className="flex justify-center mb-6">
					<CheckCircle className="h-20 w-20 text-success" />
				</div>
				<h1 className="text-3xl font-bold text-base-content mb-4">
					{t("platformPlan.success.title")}
				</h1>
				<p className="text-base-content/70 mb-4">
					{t("platformPlan.success.description")}
				</p>
				<p className="text-sm text-base-content/70 mb-6">
					{t("platformPlan.success.redirectingIn", { seconds: countdown })}
				</p>
				<Button type="button" onClick={handleRedirect}>
					{t("platformPlan.success.cta")}
				</Button>
			</div>
		</div>
	);
}
